import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { ApplicationActions } from 'widgets/application-action/ui/ApplicationActions/ApplicationActions';
import { RolesInfo } from 'widgets/roles-info/ui/RolesInfo/RolesInfo';
import { TariffsList } from 'widgets/tariffs-list/ui/TariffsList/TariffsList';

import { useApplicationQuery } from 'entities/application/api';
import { APPLICATIONS, APPLICATIONS_ENG } from 'entities/application/constants';
import { ApplicationHeaderM } from 'entities/application/ui/ApplicaionHeaderM/ApplicationHeaderM';
import { ApplicationCategoriesM } from 'entities/application/ui/ApplicationCategoriesM/ApplicationCategoriesM';
import { ApplicationConceptionsM } from 'entities/application/ui/ApplicationConceptionM/ApplicationConceptionsM';
import { ApplicationCountriesM } from 'entities/application/ui/ApplicationCountriesM/ApplicationCountriesM';
import { ApplicationDescriptionM } from 'entities/application/ui/ApplicationDescriptionM/ApplicationDescriptionM';
import { ApplicationDetailsM } from 'entities/application/ui/ApplicationDetailsM/ApplicationDetails';
import { ApplicationDeveloperM } from 'entities/application/ui/ApplicationDeveloperM/ApplicationDeveloperM';
import { ApplicationInstructions } from 'entities/application/ui/ApplicationInstruction/ApplicationInstruction';
import { ApplicationKnowledgeBase } from 'entities/application/ui/ApplicationKnowledgeBase/ApplicationKnowledgeBase';
import { ApplicationLanguagesM } from 'entities/application/ui/ApplicationLanguagesM/ApplicationLanguagesM';
import { ApplicationReviews } from 'entities/application/ui/ApplicationReviews/ApplicationReviews';
import { ApplicationSlidersM } from 'entities/application/ui/ApplicationSlidersM/ApplicationSlider';
import { ApplicationSupportM } from 'entities/application/ui/ApplicationSupportM/ApplicationSupportM';
import { ApplicationTechnicalDetails } from 'entities/application/ui/ApplicationTechnicalDetails/ApplicationTechnicalDetails';
import { useUserSubscriptionsQuery } from 'entities/tariff/api';
import { useTariffs } from 'entities/tariff/model/useTariffs';
import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import { useOnScreen } from 'features/navigation/lib/usOnScreen';
import { ApplicationMenu } from 'features/navigation/ui/ApplicationMenu/ApplicationMenu';
import { ApplicationProps } from 'pages/application/ui/Application/Application';
import { useScrollToTop } from 'shared/lib/useScrollToTop';
import { BackButton } from 'shared/ui/BackButton/BackButton';
import { ApplicationSectionMStyled } from './ApplicationM.styles';

export const ApplicationM: FC<ApplicationProps> = ({ applicationId }) => {
	const { t } = useTranslation();
	useScrollToTop();
	const instructionsRef = useRef<HTMLElement | null>(null);
	const tariffsRef = useRef<HTMLElement | null>(null);
	const detailsRef = useRef<HTMLElement | null>(null);
	const aboutRef = useRef<HTMLElement | null>(null);
	const rolesRef = useRef<HTMLElement | null>(null);
	const reviewsRef = useRef<HTMLElement | null>(null);
	const isInstructionsVisible = useOnScreen(instructionsRef);
	const isTariffsVisible = useOnScreen(tariffsRef);
	const isDetailsVisible = useOnScreen(detailsRef);
	const isAboutVisible = useOnScreen(aboutRef);
	const isRolesVisible = useOnScreen(rolesRef);
	const isReviewsVisible = useOnScreen(reviewsRef);
	const authorization = useAuthorization();
	const canManageApps = authorization?.canManageApps;
	const isDeveloper = authorization?.isDeveloper;
	const userId = authorization?.userId;
	const isBlocked = authorization?.blockAppInstallation;

	const application =
		i18next.language === `ru-RU`
			? APPLICATIONS.find(n => n.id === applicationId)
			: APPLICATIONS_ENG.find(n => n.id === applicationId);

	const [isInstalled, setIsInstalled] = useState(false);
	const tariffs = useTariffs(applicationId, !!canManageApps);
	const { data: subscriptionsInfo } = useUserSubscriptionsQuery();
	const [hasActiveSubscriptions, setHasActiveSubscriptions] = useState<boolean>(
		subscriptionsInfo
			? subscriptionsInfo.subscriptions.filter(
					n => n.applicationId === applicationId.toLowerCase() && n.subscriptionStatus === 0,
			  ).length !== 0
			: false,
	);

	const updateHasActiveSubscriptionsStatus = (): void => {
		setHasActiveSubscriptions(true);
	};

	if (!application) {
		return null;
	}
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const { data: applicationInfo } = useApplicationQuery(application.id);

	const handleInstall = (): void => {
		setIsInstalled(true);
	};

	const handleDelete = (): void => {
		setIsInstalled(false);
	};

	const handleInstruction = (): void => {
		instructionsRef.current?.scrollIntoView({ behavior: `smooth` });
	};

	const handleTariffs = (): void => {
		tariffsRef.current?.scrollIntoView({ behavior: `smooth` });
	};

	const handleDetails = (): void => {
		detailsRef.current?.scrollIntoView({ behavior: `smooth` });
	};

	const handleAbout = (): void => {
		aboutRef.current?.scrollIntoView({ behavior: `smooth` });
	};

	const handleRoles = (): void => {
		rolesRef.current?.scrollIntoView({ behavior: `smooth` });
	};

	const handleReviews = (): void => {
		reviewsRef.current?.scrollIntoView({ behavior: `smooth` });
	};

	// eslint-disable-next-line sonarjs/no-identical-functions
	const handleOverview = (): void => {
		window.scroll({
			top: 0,
			left: 0,
			behavior: `smooth`,
		});
	};

	const {
		name,
		description,
		logoUrl,
		price,
		instructions,
		languages,
		sliders,
		descriptionLong,
		contactEmail,
		isExternal,
		details,
		categories,
		conceptions,
		countries,
		developer,
		webSiteUrl,
		type,
		knowledgeBase,
		descriptionHTML,
		redirectUrl,
		plans,
		isDownloadable,
		hasFreePlan,
		isOpenForUnauthorized,
		hasSetupSupport,
		hasAdditionalFlow,
	} = application;

	const refHandlers = [
		{
			key: `instructions`,
			handler: handleInstruction,
			onScreen: isInstructionsVisible,
		},
		{
			key: `tariffs`,
			handler: handleTariffs,
			onScreen: isTariffsVisible,
		},
		{
			key: `details`,
			handler: handleDetails,
			onScreen: isDetailsVisible,
		},
		{
			key: `about`,
			handler: handleAbout,
			onScreen: isAboutVisible,
		},
		{
			key: `roles`,
			handler: handleRoles,
			onScreen: isRolesVisible,
		},
		{
			key: `overview`,
			handler: handleOverview,
			onScreen: !(isInstructionsVisible || isTariffsVisible || isDetailsVisible || isAboutVisible),
		},
		{
			key: `reviews`,
			handler: handleReviews,
			onScreen: isReviewsVisible,
		},
	];

	return (
		<ApplicationSectionMStyled>
			<BackButton />
			<header>
				<ApplicationHeaderM name={name} description={description} logoUrl={logoUrl} />
			</header>

			<ApplicationActions
				applicationName={application.name}
				userId={userId || ``}
				applicationId={applicationId}
				handleInstall={handleInstall}
				handleDelete={handleDelete}
				isInstalled={isInstalled}
				isExternal={isExternal}
				redirectUrl={redirectUrl}
				isDeveloper={!!isDeveloper}
				isOpenForUnauthorized={!!isOpenForUnauthorized}
				changeTabToTariffs={handleTariffs}
				hasActiveSubscriptions={!!tariffs.filter(n => n.subscriptions.length).length}
				plans={plans}
				isDownloadable={isDownloadable}
				hasSetupSupport={hasSetupSupport}
				hasAdditionalFlow={hasAdditionalFlow}
			/>
			<ApplicationMenu
				navItems={[
					{
						name: t(`Marketplace_Overview`),
						key: `overview`,
						included: true,
					},
					{
						name: t(`Marketplace_About_App`),
						key: `about`,
						included: true,
					},
					{
						name: t(`Marketplace_Reviews`),
						key: `reviews`,
						included: !!application.reviews,
					},
					{
						name: t(`Marketplace_Tariffs`),
						key: `tariffs`,
						included: !!application.plans,
					},
					{
						name: t(`Marketplace_Role_Access`),
						key: `roles`,
						included: !!application.plans,
					},
					{
						name: t(`Marketplace_Instruction`),
						key: `instructions`,
						included: !!application.instructions,
					},
					{
						name: t(`Marketplace_Details`),
						key: `details`,
						included: !!application.details,
					},
				]}
				refHandlers={refHandlers}
			/>
			<ApplicationSlidersM sliders={sliders} />
			<ApplicationDetailsM
				plans={!!plans}
				price={price}
				type={type}
				downloads={applicationInfo ? applicationInfo.downloadCount : null}
				languages={languages}
			/>
			<ApplicationDescriptionM description={descriptionLong} descriptionHTML={descriptionHTML} ref={aboutRef} />
			{application.reviews && <ApplicationReviews reviews={application.reviews} ref={reviewsRef} />}
			{plans && (
				<TariffsList
					applicationId={applicationId}
					handleInstall={() => handleInstall()}
					plans={plans}
					updateActiveSubscriptionsStatus={updateHasActiveSubscriptionsStatus}
					ref={tariffsRef}
					redirectUrl={redirectUrl}
					handleFreeInstall={handleInstall}
					hasActiveSubscriptions={hasActiveSubscriptions}
				/>
			)}
			{!!plans && <RolesInfo ref={rolesRef} />}
			<ApplicationInstructions
				applicationId={applicationId}
				instructions={instructions}
				ref={instructionsRef}
				showButton={!!hasFreePlan && !isBlocked}
				redirectUrl={redirectUrl}
				handleInstall={handleInstall}
			/>
			<ApplicationTechnicalDetails details={details} ref={detailsRef} />
			<ApplicationCategoriesM categories={categories} />
			<ApplicationConceptionsM conceptions={conceptions} />
			<ApplicationCountriesM countries={countries} />
			<ApplicationDeveloperM developer={developer} websiteUrl={webSiteUrl} />
			<ApplicationLanguagesM languages={languages} />
			<ApplicationSupportM contactEmail={contactEmail} />
			<ApplicationKnowledgeBase knowledgeBase={knowledgeBase} />
		</ApplicationSectionMStyled>
	);
};
